import {
  LoginResponse,
  LoginResponseType,
  NonMemberEntity,
  UserEntity,
  UserResponseEntity,
} from "@/hooks/api/generated";

export async function createOrUpdateBrevoContact(
  myGworkId: string,
  email: string,
  attributes: Record<string, string | boolean | number>
): Promise<unknown> {
  const response = await fetch("/api/brevo/contact", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ myGworkId, email, attributes }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(
      errorData?.error ||
        `Brevo API proxy failed with status ${response.status}`
    );
  }

  if (response.status === 201) {
    return response.json();
  }

  return null; // 204 — existing contact updated
}

export function todayDateString(): string {
  return new Date().toISOString().split("T")[0] || "";
}

export function getBrevoMemberType(
  isAuthenticated: boolean,
  authData?: LoginResponse | NonMemberEntity
): string {
  if (!isAuthenticated || !authData) {
    return "Non-member";
  }

  if (!authData.verifiedAt) {
    return "Unverified";
  }

  if (!authData.type) {
    return "Incomplete";
  }

  const typeLower = authData.type.toLowerCase();
  if (typeLower === LoginResponseType.professional) {
    return "Professional";
  }

  if (
    typeLower === LoginResponseType.graduate ||
    typeLower === LoginResponseType.student
  ) {
    return "Graduate";
  }

  return "Incomplete"; // fallback
}

export const brevoNewsLetter = async (
  email: string,
  firstName: string,
  lastName: string,
  organization: string,
  location: string,
  isAuthenticated: boolean,
  uuid: string,
  authData?: LoginResponse,
  country?: string
): Promise<void> => {
  try {
    const memberType = getBrevoMemberType(isAuthenticated, authData);

    await createOrUpdateBrevoContact(uuid, email, {
      FIRSTNAME: firstName || "",
      LASTNAME: lastName || "",
      EMAIL: email,
      COUNTRY: country || "",
      LOCATION: location || "",
      MEMBER_TYPE: memberType,
      ORGANISATION_NAME: organization || "",
      LAST_UPDATED: todayDateString(),
    });
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for newsletter registration:", error);
  }
};

export const brevoEmailCollection = async (
  email: string,
  isAuthenticated: boolean,
  uuid: string,
  authData?: LoginResponse,
  country?: string
): Promise<void> => {
  try {
    const memberType = getBrevoMemberType(isAuthenticated, authData);

    await createOrUpdateBrevoContact(uuid, email, {
      EMAIL: email,
      COUNTRY: country || "",
      MEMBER_TYPE: memberType,
      LAST_UPDATED: todayDateString(),
    });
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for email capture:", error);
  }
};

export const brevoEventRegistration = async (
  email: string,
  firstName: string,
  lastName: string,
  location: string,
  country: string,
  jobTitle: string,
  organization: string,
  isAuthenticated: boolean,
  uuid: string,
  authData?: LoginResponse
): Promise<void> => {
  try {
    const memberType = getBrevoMemberType(isAuthenticated, authData);

    await createOrUpdateBrevoContact(uuid, email, {
      FIRSTNAME: firstName || "",
      LASTNAME: lastName || "",
      EMAIL: email,
      LOCATION: location || "",
      COUNTRY: country || "",
      JOB_TITLE: jobTitle || "",
      MEMBER_TYPE: memberType,
      ORGANISATION_NAME: organization || "",
      LAST_UPDATED: todayDateString(),
    });
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for event registration:", error);
  }
};

export const brevoUserSignUp = async (
  email: string,
  firstName: string,
  lastName: string,
  uuid: string
): Promise<void> => {
  try {
    const memberType = "Unverified";

    await createOrUpdateBrevoContact(uuid, email, {
      FIRSTNAME: firstName || "",
      LASTNAME: lastName || "",
      EMAIL: email,
      MEMBER_TYPE: memberType,
      LAST_UPDATED: todayDateString(),
    });
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for user sign up:", error);
  }
};

export const brevoUserVerifyAccount = async (
  email: string,
  uuid: string
): Promise<void> => {
  try {
    const memberType = "Incomplete";

    await createOrUpdateBrevoContact(uuid, email, {
      EMAIL: email,
      MEMBER_TYPE: memberType,
      LAST_UPDATED: todayDateString(),
    });
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for user verify account:", error);
  }
};

export const brevoUserCompleteSignUp = async (
  userData: UserEntity,
  location?: string,
  country?: string
): Promise<void> => {
  try {
    const attributes = {
      FIRSTNAME: userData.first_name || "",
      LASTNAME: userData.last_name || "",
      EMAIL: userData.email,
      MEMBER_TYPE:
        userData.type === LoginResponseType.professional
          ? "Professional"
          : "Graduate",
      LAST_UPDATED: todayDateString(),
      ...(location && country
        ? {
            LOCATION: location,
            COUNTRY: country,
          }
        : {
            PRONOUNS: userData.pronoun || "",
          }),
    };

    await createOrUpdateBrevoContact(userData.uuid, userData.email, attributes);
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for user verify account:", error);
  }
};

export const brevoUserUpdateProfile = async (
  userData: UserResponseEntity
): Promise<void> => {
  try {
    let MEMBER_TYPE = "Incomplete";
    const typeLower = userData.type.toLowerCase();
    if (typeLower === LoginResponseType.professional) {
      MEMBER_TYPE = "Professional";
    }
    if (
      typeLower === LoginResponseType.graduate ||
      typeLower === LoginResponseType.student
    ) {
      MEMBER_TYPE = "Graduate";
    }

    const attributes: Record<string, string | number | boolean> = {
      FIRSTNAME: userData.first_name || "",
      LASTNAME: userData.last_name || "",
      EMAIL: userData.email || "",
      PRONOUNS: userData.pronoun || "",
      COUNTRY: userData.location?.country || "",
      LOCATION: userData.location?.location || "",
      AREA_OF_EXPERTISE: userData.userTaxonomy?.area?.name || "",
      INDUSTRY: userData.userTaxonomy?.industry?.name || "",
      IS_MENTEE: !!userData.userTaxonomy?.mentee_speciality,
      IS_MENTOR: !!userData.userTaxonomy?.mentor_speciality,
      IS_SPEAKER: !!userData.userTaxonomy?.is_speaker,
      JOB_TITLE: userData.userTaxonomy?.job_title || "",
      MEMBER_TYPE,
      IS_CLIENT: !!userData.userTaxonomy?.organization,
      ORGANISATION_NAME: userData.userTaxonomy.organization_name || "",
      CV_UPLOADED: !!userData.userTaxonomy?.pdf?.original_name,
      LEVEL_OF_SENIORITY: userData.userTaxonomy?.seniority || "",
      LAST_UPDATED: todayDateString(),
    };

    await createOrUpdateBrevoContact(userData.uuid, userData.email, attributes);
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for user update profile:", error);
  }
};

export const brevoTimedEmailCollection = async (
  email: string,
  isAuthenticated: boolean,
  uuid: string,
  authData?: LoginResponse,
  country?: string,
  location?: string
): Promise<void> => {
  try {
    const memberType = getBrevoMemberType(isAuthenticated, authData);

    await createOrUpdateBrevoContact(uuid, email, {
      EMAIL: email,
      COUNTRY: country || "",
      LOCATION: location || "",
      MEMBER_TYPE: memberType,
      LAST_UPDATED: todayDateString(),
    });
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for email capture:", error);
  }
};

export const brevoInlineEmailCollection = async (
  email: string,
  isAuthenticated: boolean,
  uuid: string,
  authData?: LoginResponse | NonMemberEntity,
  country?: string,
  location?: string
): Promise<void> => {
  try {
    const memberType = getBrevoMemberType(isAuthenticated, authData);

    await createOrUpdateBrevoContact(uuid, email, {
      EMAIL: email,
      COUNTRY: country || "",
      LOCATION: location || "",
      MEMBER_TYPE: memberType,
      LAST_UPDATED: todayDateString(),
    });
  } catch (error) {
    /* eslint-disable no-console */
    console.error("Brevo sync failed for inline email capture:", error);
  }
};
